import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import SEOHead from "@/components/SEOHead";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "Hvad er MySabbat?",
    answer:
      "MySabbat er en app, der hjælper dig med at planlægge dit sabbatår. Med moduler til job, rejse, budget og personlige mål får du ét samlet sted at holde styr på alt – plus en AI-guide, der viser dig næste skridt.",
  },
  {
    question: "Hvornår lanceres MySabbat?",
    answer:
      "MySabbat er på vej! Vi arbejder hårdt på at gøre appen klar til iOS og Android. Tilmeld dig vores venteliste på forsiden, så får du besked, så snart den er klar.",
  },
  {
    question: "Er MySabbat gratis?",
    answer:
      "Vi annoncerer prismodellen, når appen lanceres. Tilmeld dig ventelisten for at blive opdateret om priser og eventuelle early-bird tilbud.",
  },
  {
    question: "Hvilke funktioner har MySabbat?",
    answer:
      "MySabbat indeholder moduler til at planlægge job og arbejde, rejser og oplevelser, budget og økonomi, samt personlige mål. Du får også adgang til en AI-guide, der hjælper dig med at tage næste skridt.",
  },
  {
    question: "Hvordan fungerer AI-guiden?",
    answer:
      "AI-guiden i MySabbat analyserer dine planer og giver dig personlige anbefalinger og påmindelser. Den hjælper dig med at holde fokus og sikrer, at du ikke glemmer vigtige deadlines eller opgaver.",
  },
  {
    question: "Kan jeg bruge MySabbat på både telefon og computer?",
    answer:
      "MySabbat lanceres først som mobilapp til iOS og Android. Vi overvejer en webapp til senere, men fokuserer i første omgang på den bedste mobiloplevelse.",
  },
  {
    question: "Hvordan beskytter I mine data?",
    answer:
      "Vi tager dit privatliv alvorligt. Alle data krypteres, og vi følger GDPR og dansk databeskyttelseslovgivning. Læs mere i vores privatlivspolitik.",
  },
  {
    question: "Hvordan kontakter jeg jer?",
    answer:
      "Du kan kontakte os via kontaktformularen på vores hjemmeside eller sende en e-mail til info@mysabbat.dk. Vi svarer så hurtigt som muligt.",
  },
];

const FAQ = () => {
  const faqSection = useScrollAnimation();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Ofte stillede spørgsmål | MySabbat"
        description="Find svar på de mest almindelige spørgsmål om sabbatår, planlægning, økonomi og hvad MySabbat kan hjælpe dig med."
        path="/faq"
      />
      <Header />

      <main className="flex-1 py-12 md:py-20">
        <div className="container">
          <div className="max-w-2xl mx-auto mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Tilbage til forsiden
            </Link>
          </div>

          <div ref={faqSection.ref} className="max-w-2xl mx-auto">
            <h1
              className={`scroll-animate ${faqSection.isVisible ? "visible" : ""} text-3xl md:text-4xl font-semibold text-foreground mb-4`}
            >
              Ofte stillede spørgsmål
            </h1>
            <p
              className={`scroll-animate delay-1 ${faqSection.isVisible ? "visible" : ""} text-muted-foreground mb-10`}
            >
              Her finder du svar på de mest almindelige spørgsmål om sabbatår og MySabbat.
            </p>

            <div
              className={`scroll-animate delay-2 ${faqSection.isVisible ? "visible" : ""} bg-card rounded-2xl shadow-sm border border-border/40 overflow-hidden`}
            >
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="border-border/40"
                  >
                    <AccordionTrigger className="px-6 py-5 text-left hover:no-underline hover:bg-muted/30 transition-colors">
                      <span className="font-medium text-foreground pr-4">
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-5 pt-0">
                      <p className="text-foreground/80 leading-relaxed">
                        {faq.answer}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            <div
              className={`scroll-animate delay-3 ${faqSection.isVisible ? "visible" : ""} mt-10 text-center`}
            >
              <p className="text-muted-foreground mb-4">
                Har du et spørgsmål, der ikke er besvaret her?
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors shadow-sm"
              >
                Kontakt os
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default FAQ;
